import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import useSWR from "swr";
import { Combobox, Transition } from "@headlessui/react";
import { useDebounce } from "use-debounce";
import type { TTQueryTypes } from "@shared-libs/tiktok";
import { MdVerified } from "react-icons/md";
import { BiChevronRight } from "react-icons/bi";
import { MagnifyingGlassIcon, ArrowPathIcon, ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { RiHashtag } from "react-icons/ri";
import { FaTiktok } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Spinner } from "@/components/ui/spinner";
import { normalizeSocialHandle } from "@/utils/social-handle";

// Premium shimmer skeleton with smooth, spread-out animation
const SkeletonPulse = t0 => {
  const $ = _c(5);
  const {
    className,
    style
  } = t0;
  const t1 = `relative overflow-hidden ${className}`;
  let t2;
  if ($[0] !== style) {
    t2 = {
      background: "linear-gradient(90deg, #f3f4f6 0%, #e5e7eb 50%, #f3f4f6 100%)",
      backgroundSize: "300% 100%",
      animation: "skeleton-shimmer 2s ease-in-out infinite",
      ...style
    };
    $[0] = style;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <div className={t1} style={t2} />;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

// Skeleton loading component for search results
const SearchResultSkeleton = t0 => {
  const $ = _c(12);
  const {
    index,
    searchType
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SkeletonPulse className="h-10 w-10 shrink-0 rounded-full" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = `${70 + index * 8}%`;
  let t3;
  if ($[1] !== t2) {
    t3 = <SkeletonPulse className="h-4 rounded-md" style={{
      width: t2,
      maxWidth: "120px"
    }} />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== index || $[4] !== searchType) {
    t4 = searchType === "users" && <SkeletonPulse className="h-3 rounded-md" style={{
      width: `${85 + index * 5}%`,
      maxWidth: "160px"
    }} />;
    $[3] = index;
    $[4] = searchType;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className="flex-1 space-y-2.5">{t3}{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <SkeletonPulse className="h-5 w-5 shrink-0 rounded-sm" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <div className="flex items-center space-x-3 px-4 py-3">{t1}{t5}{t6}</div>;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};

/**
 * Unified search result row that crossfades between skeleton and real content
 */
function SearchResultRowWithTransition(t0) {
  const $ = _c(24);
  const {
    result,
    index,
    isLoading,
    searchType,
    totalResults,
    isActive: t1
  } = t0;
  const isActive = t1 === undefined ? false : t1;
  const showSkeleton = isLoading || !result;
  const isLastItem = index === totalResults - 1;
  const t2 = `relative ${!isLastItem ? "border-b border-gray-50/50" : ""}`;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 1
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const t4 = showSkeleton ? 1 : 0;
  let t5;
  if ($[1] !== t4) {
    t5 = {
      opacity: t4
    };
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      duration: 0.2
    };
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const t7 = showSkeleton ? "relative" : "pointer-events-none absolute inset-0";
  let t8;
  if ($[4] !== index || $[5] !== searchType) {
    t8 = <SearchResultSkeleton index={index} searchType={searchType} />;
    $[4] = index;
    $[5] = searchType;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t5 || $[8] !== t7 || $[9] !== t8) {
    t9 = <motion.div initial={t3} animate={t5} transition={t6} className={t7}>{t8}</motion.div>;
    $[7] = t5;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== isActive || $[12] !== result || $[13] !== showSkeleton) {
    t10 = result && "unique_id" in result && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: showSkeleton ? 0 : 1
    }} transition={{
      duration: 0.25,
      delay: 0.1
    }} className={showSkeleton ? "pointer-events-none absolute inset-0" : "relative"}><div className={`flex cursor-pointer items-center space-x-3 px-4 py-3 transition-colors duration-150 ease-out ${isActive ? "bg-gray-50" : ""}`}><div className="relative shrink-0"><Image unoptimized={true} src={result.avatar_url || "/img/default_avatar.jpeg"} alt={result.unique_id} width={40} height={40} className="h-10 w-10 rounded-full object-cover ring-2 ring-gray-100" onError={_temp} />{result.is_verified && <div className="absolute -bottom-0.5 -right-0.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-1 ring-white"><MdVerified className="h-3.5 w-3.5 text-[#20d5ec]" /></div>}</div><div className="flex w-full min-w-0 items-center justify-between"><div className="min-w-0"><p className="truncate font-medium text-gray-900">@{result.unique_id?.trim()}</p>{result.nickname?.trim() && <p className="truncate text-sm text-gray-500">{result.nickname.trim()}</p>}</div><BiChevronRight className="h-5 w-5 shrink-0 text-gray-400" /></div></div></motion.div>;
    $[11] = isActive;
    $[12] = result;
    $[13] = showSkeleton;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== isActive || $[16] !== result || $[17] !== showSkeleton) {
    t11 = result && "name" in result && !("unique_id" in result) && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: showSkeleton ? 0 : 1
    }} transition={{
      duration: 0.25,
      delay: 0.1
    }} className={showSkeleton ? "pointer-events-none absolute inset-0" : "relative"}><div className={`flex cursor-pointer items-center space-x-3 px-4 py-3 transition-colors duration-150 ease-out ${isActive ? "bg-gray-50" : ""}`}><div className="bg-linear-to-br from-tiktok-cyan flex h-10 w-10 shrink-0 items-center justify-center rounded-full to-tiktok-red/20"><RiHashtag className="h-5 w-5 text-tiktok-red" /></div><div className="flex w-full min-w-0 items-center justify-between"><div className="min-w-0"><p className="truncate font-medium text-gray-900">#{result.name}</p>{result.view_count > 0 && <p className="text-xs text-gray-400">{result.view_count.toLocaleString()} views</p>}</div><BiChevronRight className="h-5 w-5 shrink-0 text-gray-400" /></div></div></motion.div>;
    $[15] = isActive;
    $[16] = result;
    $[17] = showSkeleton;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t2 || $[22] !== t9) {
    t12 = <div className={t2}>{t9}{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t2;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  return t12;
}

// Empty state component
function _temp(e) {
  const target = e.target as HTMLImageElement;
  target.onerror = null;
  target.src = "/img/default_avatar.jpeg";
}
const EmptyState = t0 => {
  const $ = _c(12);
  const {
    query,
    searchType
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      scale: 0.95
    };
    t2 = {
      opacity: 1,
      scale: 1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== searchType) {
    t3 = <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">{searchType === "users" ? <FaTiktok className="h-6 w-6 text-gray-400" /> : <RiHashtag className="h-6 w-6 text-gray-400" />}</div>;
    $[2] = searchType;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== query) {
    t4 = <p className="text-sm font-medium text-gray-700">No results for "{query}"</p>;
    $[4] = query;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = searchType === "users" ? "Try searching for a username like \"charlidamelio\"" : "Try searching for a hashtag like \"dance\"";
  let t6;
  if ($[6] !== t5) {
    t6 = <p className="mt-1 text-xs text-gray-500">{t5}</p>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t4 || $[10] !== t6) {
    t7 = <motion.div initial={t1} animate={t2} className="px-5 py-8 text-center">{t3}{t4}{t6}</motion.div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return t7;
};

// Error state component
const ErrorState = t0 => {
  const $ = _c(7);
  const {
    onRetry
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0,
      scale: 0.95
    };
    t2 = {
      opacity: 1,
      scale: 1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50"><ExclamationCircleIcon className="h-6 w-6 text-red-400" /></div>;
    t4 = <p className="text-sm font-medium text-gray-700">Unable to search right now</p>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowPathIcon className="h-3.5 w-3.5" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== onRetry) {
    t6 = <motion.div initial={t1} animate={t2} className="px-5 py-6 text-center">{t3}{t4}<button onClick={onRetry} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-tiktok-red transition-colors hover:bg-red-100">{t5}Try again</button></motion.div>;
    $[5] = onRetry;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};
export interface TiktokAccount {
  user_id: string;
  unique_id: string;
  nickname?: string;
  avatar_url?: string;
  is_verified?: boolean;
}
export interface TiktokHashtag {
  hashtag_id: string;
  name: string;
  view_count: number;
  video_count: number;
}
type TiktokSearchResult = TiktokAccount | TiktokHashtag;

// Dropdown container styles (shared between skeleton and results)
const dropdownClassName = `
  absolute left-0 top-full mt-2 max-h-[300px] overflow-auto rounded-2xl z-50 w-full bg-white
  border border-gray-100 shadow-2xl shadow-black/10
  scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent
  ring-1 ring-black/5
`;

/**
 * TikTok Search Field - Simplified Implementation
 *
 * State architecture:
 * - inputValue: What user sees in input (updates immediately on keypress)
 * - debouncedValue: Auto-debounced version of inputValue (triggers API after 300ms pause)
 * - selectedResult: The currently selected result from dropdown
 * - isFocused: Whether input has focus
 * - results: Array of search results from API
 *
 * Key insight: "isConfirmed" is DERIVED, not tracked:
 * - A selection is "confirmed" when inputValue matches the selected result's value
 * - This naturally prevents searches when user hasn't modified their selection
 * - When they start typing, inputValue diverges from selection, enabling search
 */
export default function TikTokSearchField(t0) {
  const $ = _c(115);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let props;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== t1) {
    ({
      onResultSelected: t2,
      onValueChange: t3,
      onEnterKeyPressed: t4,
      disabled: t5,
      autocompleteOnSelect: t6,
      searchType: t7,
      autoFocus: t8,
      disableSearch: t9,
      defaultValue: t10,
      ...props
    } = t1);
    $[2] = t1;
    $[3] = props;
    $[4] = t10;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    props = $[3];
    t10 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t11;
  if ($[13] !== t2) {
    t11 = t2 === undefined ? _temp2 : t2;
    $[13] = t2;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const onResultSelected = t11;
  let t12;
  if ($[15] !== t3) {
    t12 = t3 === undefined ? _temp3 : t3;
    $[15] = t3;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const onValueChange = t12;
  let t13;
  if ($[17] !== t4) {
    t13 = t4 === undefined ? _temp4 : t4;
    $[17] = t4;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const onEnterKeyPressed = t13;
  const disabled = t5 === undefined ? false : t5;
  const autocompleteOnSelect = t6 === undefined ? true : t6;
  const searchType = t7 === undefined ? "users" : t7;
  const autoFocus = t8 === undefined ? true : t8;
  const disableSearch = t9 === undefined ? false : t9;
  const defaultValue = t10 === undefined ? "" : t10;
  const [inputValue, setInputValue] = useState(defaultValue);
  const [selectedResult, setSelectedResult] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  let t14;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const [results, setResults] = useState(t14);
  const lastInteractionRef = useRef(0);
  const [debouncedValue] = useDebounce(inputValue, 300);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  let t15;
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      const markInteraction = () => {
        lastInteractionRef.current = Date.now();
      };
      document.addEventListener("mousedown", markInteraction);
      document.addEventListener("touchstart", markInteraction, {
        passive: true
      });
      return () => {
        document.removeEventListener("mousedown", markInteraction);
        document.removeEventListener("touchstart", markInteraction);
      };
    };
    t16 = [];
    $[20] = t15;
    $[21] = t16;
  } else {
    t15 = $[20];
    t16 = $[21];
  }
  useEffect(t15, t16);
  const getResultValue = _temp5;
  let t17;
  if ($[22] !== inputValue || $[23] !== selectedResult) {
    t17 = selectedResult && inputValue === getResultValue(selectedResult);
    $[22] = inputValue;
    $[23] = selectedResult;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  const isConfirmed = t17;
  const searchKey = !disableSearch && debouncedValue.length >= 2 && !isConfirmed ? debouncedValue : null;
  const isPendingSearch = inputValue.length >= 2 && !isConfirmed && inputValue !== debouncedValue;
  let t18;
  if ($[25] !== searchType) {
    t18 = async query => {
      console.log("\uD83D\uDD0D TikTok API fetch for:", query);
      const targets = await fetch(`/api/tiktok/search-targets?type=${searchType as TTQueryTypes}&search=${query}`).then(_temp6);
      console.log(`✅ Got ${Array.isArray(targets) ? targets.length : 0} TikTok results for: ${query}`);
      const resultArray = Array.isArray(targets) ? targets : [];
      setResults(resultArray);
      return resultArray;
    };
    $[25] = searchType;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const fetchFromRapidAPI = t18;
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      onError: _temp7,
      errorRetryCount: 2,
      revalidateOnFocus: false,
      dedupingInterval: 500
    };
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const {
    error: error_0,
    isValidating,
    mutate: revalidate
  } = useSWR(searchKey, fetchFromRapidAPI, t19);
  const isLoading = isPendingSearch || isValidating;
  const hasError = !!error_0 && debouncedValue.length >= 2 && !isConfirmed;
  const hasResults = !isLoading && !hasError && results.length > 0;
  const showEmpty = !isLoading && !hasError && results.length === 0 && debouncedValue.length >= 2 && !isConfirmed && searchKey !== null;
  const showDropdown = isFocused && inputValue.length >= 2 && !isConfirmed;
  let t20;
  let t21;
  if ($[28] !== autoFocus) {
    t20 = () => {
      if (inputRef.current && !inputRef.current.value && autoFocus) {
        inputRef.current.focus();
      }
    };
    t21 = [autoFocus];
    $[28] = autoFocus;
    $[29] = t20;
    $[30] = t21;
  } else {
    t20 = $[29];
    t21 = $[30];
  }
  useEffect(t20, t21);
  let t22;
  if ($[31] !== defaultValue || $[32] !== selectedResult) {
    t22 = () => {
      if (defaultValue && inputRef.current) {
        inputRef.current.value = defaultValue;
        setInputValue(defaultValue);
        if (!selectedResult) {
          setSelectedResult({
            unique_id: defaultValue,
            user_id: ""
          } as TiktokAccount);
        }
      }
    };
    $[31] = defaultValue;
    $[32] = selectedResult;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== defaultValue) {
    t23 = [defaultValue];
    $[34] = defaultValue;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  useEffect(t22, t23);
  const compareResults = _temp8;
  let t24;
  if ($[36] !== onValueChange) {
    t24 = value_0 => {
      setInputValue(value_0);
      onValueChange(value_0);
    };
    $[36] = onValueChange;
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  const handleInputChange = t24;
  let t25;
  if ($[38] !== revalidate || $[39] !== searchKey) {
    t25 = () => {
      if (searchKey) {
        revalidate();
      }
    };
    $[38] = revalidate;
    $[39] = searchKey;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  const handleRetry = t25;
  let t26;
  if ($[41] !== autocompleteOnSelect || $[42] !== onResultSelected || $[43] !== onValueChange) {
    t26 = result_1 => {
      if (!result_1) {
        return;
      }
      console.log("Selected TikTok result:", result_1);
      setSelectedResult(result_1);
      onResultSelected(result_1);
      const value_1 = getResultValue(result_1);
      onValueChange(value_1);
      setIsFocused(false);
      if (autocompleteOnSelect) {
        setInputValue(value_1);
        if (inputRef.current) {
          inputRef.current.value = value_1;
        }
      } else {
        setInputValue("");
        setSelectedResult(null);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
      }
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }, 10);
    };
    $[41] = autocompleteOnSelect;
    $[42] = onResultSelected;
    $[43] = onValueChange;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  const handleResultSelect = t26;
  const t27 = disabled || (props as {
    disabled?: boolean;
  }).disabled;
  let t28;
  if ($[45] !== handleResultSelect) {
    t28 = result_2 => {
      if (result_2) {
        handleResultSelect(result_2);
      }
    };
    $[45] = handleResultSelect;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  let t29;
  if ($[47] !== isConfirmed || $[48] !== isFocused || $[49] !== searchType || $[50] !== selectedResult) {
    t29 = searchType === "users" ? <div className="absolute left-3.5 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center"><AnimatePresence mode="wait" initial={false}>{selectedResult && "avatar_url" in selectedResult && isConfirmed ? <motion.div key="avatar" initial={{
          scale: 0.6,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.6,
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1]
        }}><div className="relative h-7 w-7"><Image unoptimized={true} src={selectedResult.avatar_url || "/img/default_avatar.jpeg"} alt={selectedResult.unique_id} width={28} height={28} className="h-full w-full rounded-full object-cover ring-2 ring-white shadow-sm" onError={_temp9} /></div></motion.div> : <motion.div key="icon" initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.8,
          opacity: 0
        }} transition={{
          duration: 0.15
        }}><FaTiktok className={`h-5 w-5 transition-colors duration-200 ${isFocused ? "text-gray-900" : "text-gray-400"}`} /></motion.div>}</AnimatePresence></div> : <MagnifyingGlassIcon className={`absolute left-5 top-1/2 z-10 h-5 w-5 -translate-y-1/2 transform transition-colors duration-200 ${isFocused ? "text-tiktok-red" : "text-gray-500"}`} />;
    $[47] = isConfirmed;
    $[48] = isFocused;
    $[49] = searchType;
    $[50] = selectedResult;
    $[51] = t29;
  } else {
    t29 = $[51];
  }
  const t30 = `${searchType === "users" ? "charlidamelio" : "dance"}`;
  let t31;
  let t32;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = () => setIsFocused(true);
    t32 = event => {
      event.preventDefault();
      const timeSinceInteraction = Date.now() - lastInteractionRef.current;
      if (timeSinceInteraction > 200 && !event.relatedTarget) {
        return;
      }
      setTimeout(() => {
        if (dropdownRef.current?.contains(document.activeElement)) {
          return;
        }
        setIsFocused(false);
      }, 150);
    };
    $[52] = t31;
    $[53] = t32;
  } else {
    t31 = $[52];
    t32 = $[53];
  }
  let t33;
  if ($[54] !== handleInputChange) {
    t33 = e_1 => handleInputChange(e_1.target.value);
    $[54] = handleInputChange;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== onEnterKeyPressed) {
    t34 = e_2 => {
      if (e_2.key === "Tab") {
        e_2.preventDefault();
        const focusable = Array.from(document.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])"));
        const currentIndex = focusable.indexOf(document.activeElement as Element);
        const nextIndex = (currentIndex + 1) % focusable.length;
        const nextElement = focusable[nextIndex] as HTMLElement;
        nextElement.focus();
      }
      if (e_2.key === "Enter") {
        e_2.preventDefault();
        setIsFocused(false);
        if (onEnterKeyPressed) {
          onEnterKeyPressed();
        }
      }
      const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Escape"];
      if (allowedKeys.includes(e_2.key)) {
        return;
      }
      const regex = /^[a-zA-Z0-9_.]*$/;
      if (!regex.test(e_2.key)) {
        e_2.preventDefault();
      }
    };
    $[56] = onEnterKeyPressed;
    $[57] = t34;
  } else {
    t34 = $[57];
  }
  let t35;
  if ($[58] !== handleInputChange) {
    t35 = e_3 => {
      e_3.preventDefault();
      const text = e_3.clipboardData.getData("text");
      const parsed = normalizeSocialHandle(text, "tiktok");
      const nextValue = parsed.ok ? parsed.handle : text.replace(/[\r\n\t]+/g, " ").trim().slice(0, 200);
      if (e_3.target instanceof HTMLInputElement) {
        e_3.target.value = nextValue;
        handleInputChange(nextValue);
      }
    };
    $[58] = handleInputChange;
    $[59] = t35;
  } else {
    t35 = $[59];
  }
  let t36;
  if ($[60] !== inputValue) {
    t36 = () => inputValue;
    $[60] = inputValue;
    $[61] = t36;
  } else {
    t36 = $[61];
  }
  const t37 = `block h-14 w-full rounded-[16px] border-0 bg-[#f5f5f5] px-4 py-3 pl-[52px] text-base font-semibold tracking-[0.009em] text-gray-900 outline-none transition-all duration-200 ease-out placeholder:font-normal placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-inset focus:ring-gray-900 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-50 ${disabled ? "bg-gray-100" : ""} `;
  let t38;
  if ($[62] !== autoFocus || $[63] !== disabled || $[64] !== props || $[65] !== t30 || $[66] !== t33 || $[67] !== t34 || $[68] !== t35 || $[69] !== t36 || $[70] !== t37) {
    t38 = <Combobox.Button as="div" className="flex"><Combobox.Input ref={inputRef} placeholder={t30} onFocus={t31} onBlur={t32} onClick={_temp10} onChange={t33} onKeyDown={t34} onPaste={t35} displayValue={t36} className={t37} required={autoFocus} readOnly={disabled} autoComplete="off" autoCorrect="off" autoCapitalize="off" data-1p-ignore={true} data-lpignore="true" data-bwignore={true} data-form-type="other" data-protonpass-ignore={true} spellCheck={false} maxLength={50} minLength={2} autoFocus={autoFocus} {...props} /></Combobox.Button>;
    $[62] = autoFocus;
    $[63] = disabled;
    $[64] = props;
    $[65] = t30;
    $[66] = t33;
    $[67] = t34;
    $[68] = t35;
    $[69] = t36;
    $[70] = t37;
    $[71] = t38;
  } else {
    t38 = $[71];
  }
  let t39;
  if ($[72] !== isLoading) {
    t39 = isLoading && <motion.div initial={{
      opacity: 0,
      x: 4
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: 4
    }} transition={{
      duration: 0.2,
      ease: [0.22, 1, 0.36, 1]
    }} className="absolute right-4 top-1/2 z-10 flex -translate-y-1/2 items-center gap-2"><span className="text-xs font-medium text-gray-400">Searching</span><Spinner size="sm" className="text-gray-950" /></motion.div>;
    $[72] = isLoading;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  let t40;
  if ($[74] !== t39) {
    t40 = <AnimatePresence>{t39}</AnimatePresence>;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== t29 || $[77] !== t38 || $[78] !== t40) {
    t41 = <div className="group relative"><div className="relative"><div className="relative">{t29}{t38}{t40}</div></div></div>;
    $[76] = t29;
    $[77] = t38;
    $[78] = t40;
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  const t42 = showDropdown && (isLoading || hasResults);
  const t43 = isLoading ? 0.92 : 1;
  let t44;
  if ($[80] !== t43) {
    t44 = {
      opacity: t43,
      transition: "opacity 300ms cubic-bezier(0.22, 1, 0.36, 1)"
    };
    $[80] = t43;
    $[81] = t44;
  } else {
    t44 = $[81];
  }
  let t45;
  if ($[82] !== results.length) {
    t45 = Array.from({
      length: Math.max(4, results.length)
    });
    $[82] = results.length;
    $[83] = t45;
  } else {
    t45 = $[83];
  }
  let t46;
  if ($[84] !== isLoading || $[85] !== results || $[86] !== searchType || $[87] !== t45) {
    t46 = t45.map((_, index) => {
      const result_3 = results[index] || null;
      const key = result_3 ? "unique_id" in result_3 ? result_3.unique_id : result_3.name : `skeleton-${index}`;
      return <Combobox.Option key={key} value={result_3} disabled={!result_3} className="outline-none">{t47 => {
          const {
            active
          } = t47;
          return <SearchResultRowWithTransition result={result_3} index={index} isLoading={isLoading} searchType={searchType} totalResults={Math.max(4, results.length)} isActive={active} />;
        }}</Combobox.Option>;
    });
    $[84] = isLoading;
    $[85] = results;
    $[86] = searchType;
    $[87] = t45;
    $[88] = t46;
  } else {
    t46 = $[88];
  }
  let t47;
  if ($[89] !== t44 || $[90] !== t46) {
    t47 = <Combobox.Options static={true} className={dropdownClassName} ref={dropdownRef} style={t44}>{t46}</Combobox.Options>;
    $[89] = t44;
    $[90] = t46;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] !== t42 || $[93] !== t47) {
    t48 = <Transition as="div" className="relative" show={t42} enter="transition-all duration-200 ease-out" enterFrom="opacity-0 translate-y-1" enterTo="opacity-100 translate-y-0" leave="transition-all duration-150 ease-in" leaveFrom="opacity-100 translate-y-0" leaveTo="opacity-0 translate-y-1">{t47}</Transition>;
    $[92] = t42;
    $[93] = t47;
    $[94] = t48;
  } else {
    t48 = $[94];
  }
  let t49;
  if ($[95] !== selectedResult || $[96] !== t27 || $[97] !== t28 || $[98] !== t41 || $[99] !== t48) {
    t49 = <Combobox value={selectedResult} by={compareResults} disabled={t27} onChange={t28}>{t41}{t48}</Combobox>;
    $[95] = selectedResult;
    $[96] = t27;
    $[97] = t28;
    $[98] = t41;
    $[99] = t48;
    $[100] = t49;
  } else {
    t49 = $[100];
  }
  let t50;
  if ($[101] !== handleRetry || $[102] !== hasError || $[103] !== hasResults || $[104] !== inputValue || $[105] !== isLoading || $[106] !== searchType || $[107] !== showDropdown || $[108] !== showEmpty) {
    t50 = showDropdown && (showEmpty || hasError) && !isLoading && !hasResults && <motion.div initial={{
      opacity: 0,
      y: 4
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 4
    }} transition={{
      duration: 0.15
    }} className={dropdownClassName}>{hasError && <ErrorState onRetry={handleRetry} />}{showEmpty && !hasError && <EmptyState query={inputValue} searchType={searchType} />}</motion.div>;
    $[101] = handleRetry;
    $[102] = hasError;
    $[103] = hasResults;
    $[104] = inputValue;
    $[105] = isLoading;
    $[106] = searchType;
    $[107] = showDropdown;
    $[108] = showEmpty;
    $[109] = t50;
  } else {
    t50 = $[109];
  }
  let t51;
  if ($[110] !== t50) {
    t51 = <div className="relative"><AnimatePresence>{t50}</AnimatePresence></div>;
    $[110] = t50;
    $[111] = t51;
  } else {
    t51 = $[111];
  }
  let t52;
  if ($[112] !== t49 || $[113] !== t51) {
    t52 = <div className="relative">{t49}{t51}</div>;
    $[112] = t49;
    $[113] = t51;
    $[114] = t52;
  } else {
    t52 = $[114];
  }
  return t52;
}
function _temp10(e_0) {
  if ((e_0.relatedTarget as Element)?.id?.includes("headlessui-combobox-button")) {
    return;
  }
  !open && (e_0.currentTarget.nextSibling as HTMLElement)?.click();
}
function _temp9(e) {
  const target = e.target as HTMLImageElement;
  target.onerror = null;
  target.src = "/img/default_avatar.jpeg";
}
function _temp8(a, b) {
  if (a && b) {
    if ("unique_id" in a && "unique_id" in b) {
      return a.unique_id.toLowerCase() === b.unique_id.toLowerCase();
    } else {
      if ("name" in a && "name" in b) {
        return a.name.toLowerCase() === b.name.toLowerCase();
      }
    }
  }
  return false;
}
function _temp7(error) {
  return console.error("SWR error:", error);
}
function _temp6(res) {
  return res.json();
}
function _temp5(result_0) {
  if (!result_0) {
    return "";
  }
  return "unique_id" in result_0 ? result_0.unique_id : result_0.name;
}
function _temp4() {}
function _temp3(value) {}
function _temp2(result) {}